/**
 * SHA-256 fuer die Beweisfuehrung des Rechnungsmoduls.
 *
 * Jede ausgestellte Rechnung wird als PDF-Bytes archiviert und ueber ihren
 * Hash identifizierbar gemacht. Der Hash beweist nichts von sich aus — er macht
 * eine nachtraegliche Aenderung nur feststellbar. Das Rechtekonzept (RLS,
 * Festschreibungs-Trigger, Append-only-Audit-Log) traegt die eigentliche
 * Absicherung; der Hash ist die Kontrollinstanz darueber.
 */

/**
 * Wirft, wenn die Web-Crypto-API fehlt.
 *
 * crypto.subtle gibt es nur in einem Secure Context (HTTPS oder localhost).
 * Produktion laeuft auf HTTPS, die Entwicklung auf localhost — beides erfuellt
 * die Bedingung. Faellt das trotzdem aus (alter WebView, Zugriff ueber eine
 * nackte IP im LAN), brechen wir ab, statt eine ungehashte Rechnung
 * auszustellen: ein Beleg ohne Integritaetsnachweis ist schlimmer als gar
 * keiner.
 */
export const assertCryptoAvailable = (): void => {
    if (typeof crypto === 'undefined' || !crypto?.subtle) {
        throw new Error(
            'Rechnungen koennen nicht ausgestellt werden: Die Krypto-Funktionen des Browsers ' +
                'sind nicht verfuegbar. Das passiert, wenn die Anwendung ohne HTTPS aufgerufen ' +
                'wird. Bitte die Seite ueber https:// oder http://localhost oeffnen.',
        );
    }
};

/** True, wenn gehasht werden kann. Fuer UI-Vorabpruefungen, die nicht werfen sollen. */
export const isCryptoAvailable = (): boolean =>
    typeof crypto !== 'undefined' && !!crypto?.subtle;

/**
 * SHA-256 als Kleinbuchstaben-Hex-String (64 Zeichen).
 *
 * Der Umweg ueber byteOffset/byteLength ist noetig, weil eine Uint8Array ein
 * Ausschnitt eines groesseren ArrayBuffers sein kann. `bytes.buffer` direkt zu
 * uebergeben wuerde in dem Fall den gesamten Puffer hashen statt nur den
 * gemeinten Ausschnitt.
 */
export const sha256Hex = async (bytes: Uint8Array | ArrayBuffer): Promise<string> => {
    assertCryptoAvailable();
    const buffer =
        bytes instanceof Uint8Array
            ? bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
            : bytes;
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
};

/** SHA-256 eines Textes (UTF-8 kodiert) — fuer XML und Briefpapier-Fingerabdruecke. */
export const sha256HexOfText = async (text: string): Promise<string> =>
    sha256Hex(new TextEncoder().encode(text));

/**
 * Vergleicht zwei Hex-Hashes ohne Ruecksicht auf Gross-/Kleinschreibung.
 *
 * Bewusst kein zeitkonstanter Vergleich: hier wird kein Geheimnis geprueft,
 * sondern die Unversehrtheit eines Archivdokuments. Ein Timing-Seitenkanal
 * haette nichts zu verraten.
 */
export const hashesMatch = (a?: string | null, b?: string | null): boolean =>
    !!a && !!b && a.toLowerCase() === b.toLowerCase();
